/**
 * Port of RenderingViewModel.ApplyColor + PropertyMapping (Nanome.Adapter/Display/RenderingViewModel.Coloring.cs:54-383)
 * over a NanomeModel. Returns linear-free sRGB floats (the shaders convert to linear).
 */
import type { ColoringModel, PropertySource } from '@nanome-lite/state';
import type { NanomeModel } from '@nanome-lite/data';
import { isPolymer } from '@nanome-lite/data/model';
import {
  ELEMENT_COLORS, ELEMENT_COLOR_DEFAULT, RESIDUE_COLORS, RESIDUE_COLOR_DEFAULT, SS_COLORS, PALETTES, HBOND_COLORS,
  SecondaryStructure, chainInstanceKey, modelInstanceKey, paletteIndexed, paletteInterpolated, hexFromApi, hexToFloat,
  type Hex,
} from '@nanome-lite/look';

export interface ColoringContext {
  entrySerial: number;
  /** 1-based current model serial of the entry */
  modelSerial: number;
  /**
   * Workspace chain-name index (MapChainNames, RenderingViewModel.Coloring.cs:274-285): unique chain names over all
   * entries in order. Without it ChainName falls back to the chain's index within this model.
   */
  chainNameIndex?: ReadonlyMap<string, number>;
}

/** ChainName key and key count (GetChainNameIndex / GetChainNameNormalized, Coloring.cs:343-358). */
function chainNameKey(model: NanomeModel, chainIndex: number, ctx: ColoringContext): [number, number] {
  const idx = ctx.chainNameIndex?.get(model.chains.name[chainIndex] ?? '');
  if (idx === undefined || !ctx.chainNameIndex) return [chainIndex, model.chains.count];
  return [idx, ctx.chainNameIndex.size];
}

/**
 * Nanome reads isAcceptor from PotentialHydrogenBondDonors and isDonor from PotentialHydrogenBondAcceptors
 * (RenderingViewModel.Coloring.cs:94-95), so chemically donating nitrogens show the acceptor colour and oxygens the
 * donor colour. Reproduced for parity; set to false for the chemically correct mapping.
 */
export const REPRODUCE_HBOND_DONOR_ACCEPTOR_SWAP = true;

/** Kyte-Doolittle hydropathicity (RenderingViewModel.Coloring.cs:28-50); hydrophobic when > 0. */
const KD_HYDROPHOBICITY: Record<string, number> = {
  ILE: 4.5, VAL: 4.2, LEU: 3.8, PHE: 2.8, CYS: 2.5, MET: 1.9, ALA: 1.8, GLY: -0.4, THR: -0.7, SER: -0.8, TRP: -0.9,
  TYR: -1.3, PRO: -1.6, HIS: -3.2, GLU: -3.5, GLN: -3.5, ASP: -3.5, ASN: -3.5, LYS: -3.9, ARG: -4.5,
};

/** Side-chain aromatic ring atoms by residue (approximation of the server's aromatic set). */
const AROMATIC_RING_ATOMS: Record<string, Set<string>> = {
  PHE: new Set(['CG', 'CD1', 'CD2', 'CE1', 'CE2', 'CZ']),
  TYR: new Set(['CG', 'CD1', 'CD2', 'CE1', 'CE2', 'CZ']),
  TRP: new Set(['CG', 'CD1', 'CD2', 'NE1', 'CE2', 'CE3', 'CZ2', 'CZ3', 'CH2']),
  HIS: new Set(['CG', 'ND1', 'CD2', 'CE1', 'NE2']),
};

const SS_NAME_BY_VALUE: Record<number, string> = Object.fromEntries(Object.entries(SecondaryStructure).map(([k, v]) => [v, k]));

/** Default palette and palette type per PropertySource (Coloring.cs factories, Domain Colors/Coloring.cs:9-39). */
function defaultPalette(source: PropertySource): { palette: readonly Hex[]; paletteType: 'Default' | 'Interpolated' } {
  switch (source) {
    case 'AtomSerial': return { palette: PALETTES.Rainbow, paletteType: 'Interpolated' };
    case 'BFactor': return { palette: PALETTES.ColdToWarm, paletteType: 'Interpolated' };
    case 'Occupancy': return { palette: PALETTES.ColdToWarm, paletteType: 'Default' };
    case 'ChainName': return { palette: PALETTES.UniqueThirty, paletteType: 'Default' };
    case 'ChainInstance': return { palette: PALETTES.UniqueLowSaturated, paletteType: 'Default' };
    case 'ModelInstance': return { palette: PALETTES.UniqueLowSaturated, paletteType: 'Default' };
    case 'AtomName': return { palette: PALETTES.UniqueThirty, paletteType: 'Default' };
  }
}

/** Atom serial range over polymer atoms, all atoms when there are none (PropertyMapping.MapAtomSerialRange :287-314). */
export function atomSerialRange(model: NanomeModel): [number, number] {
  let min = Infinity, max = -Infinity, any = false;
  for (let i = 0; i < model.atomCount; i++) {
    if (!isPolymer(model.substructure[i]!)) continue;
    const s = model.atomSerial[i]!;
    any = true; if (s < min) min = s; if (s > max) max = s;
  }
  if (!any) for (let i = 0; i < model.atomCount; i++) { const s = model.atomSerial[i]!; if (s < min) min = s; if (s > max) max = s; }
  if (!Number.isFinite(min)) return [0, 0];
  return [min, max];
}

const EPSILON = 0.001; // PropertyMapping.epsilon
function normalise(v: number, min: number, max: number, clamp: boolean): number {
  let div = max - min;
  if (Math.abs(div) < EPSILON) div = 1;
  const t = (v - min) / div;
  return clamp ? Math.min(1, Math.max(0, t)) : t;
}

const WHITE: [number, number, number] = [1, 1, 1];

/** Per-atom colour for one coloring; `indexInSelection` is the atom's position in the component's atom list. */
export function colorOfAtom(
  model: NanomeModel, atomIndex: number, indexInSelection: number, coloring: ColoringModel, ctx: ColoringContext,
  cache: { serialRange?: [number, number]; rangeIndex?: Map<number, Hex> } = {},
): [number, number, number] {
  const el = model.element[atomIndex]!;
  switch (coloring.kind) {
    case 'ElementType': {
      // ElementType(carbonColoring): carbons take the inner coloring (Coloring.cs:67-74). The DTO expresses the inner
      // coloring as `carbonsOnly` on a Property/other coloring, so this branch is plain CPK.
      return hexToFloat(ELEMENT_COLORS.get(el) ?? ELEMENT_COLOR_DEFAULT);
    }
    case 'Uniform':
      return hexToFloat(coloring.uniformColor ? hexFromApi(coloring.uniformColor) : 0xffffff);
    case 'SecondaryStructure': {
      const ss = model.residues.ss[model.residueIndex[atomIndex]!]!;
      return hexToFloat(SS_COLORS[SS_NAME_BY_VALUE[ss] ?? 'NotAssigned'] ?? 0xffffff);
    }
    case 'ResidueType': {
      const name = model.residues.name[model.residueIndex[atomIndex]!] ?? '';
      return hexToFloat(RESIDUE_COLORS[name.toUpperCase()] ?? RESIDUE_COLOR_DEFAULT);
    }
    case 'Enumerated': {
      // colorRanges: colour by file atom serial (EnumeratedAtoms-style ranges); else colors[indexInSelection % n] (:81-82)
      if (coloring.colorRanges && coloring.colorRanges.length > 0) {
        if (!cache.rangeIndex) {
          cache.rangeIndex = new Map();
          for (const cr of coloring.colorRanges) {
            const c = hexFromApi(cr.color);
            for (const s of cr.serials.sets) cache.rangeIndex.set(s, c);
            for (const [a, b] of cr.serials.ranges) for (let s = a; s <= b; s++) cache.rangeIndex.set(s, c);
          }
        }
        const c = cache.rangeIndex.get(model.atomSerial[atomIndex]!);
        return c === undefined ? WHITE : hexToFloat(c);
      }
      if (coloring.colors && coloring.colors.length > 0) return hexToFloat(hexFromApi(coloring.colors[indexInSelection % coloring.colors.length]!));
      return WHITE;
    }
    case 'HydrogenBonding': {
      const resName = (model.residues.name[model.residueIndex[atomIndex]!] ?? '').toUpperCase();
      const atomName = model.atomName[atomIndex] ?? '';
      const isHydrophobic = (KD_HYDROPHOBICITY[resName] ?? -1) > 0;
      // Approximate donor/acceptor sets by element: N (and S-H) donate, O (and N without H in HIS/aromatic) accept.
      let chemDonor = el === 7 || (el === 16 && resName === 'CYS');
      let chemAcceptor = el === 8;
      if (el === 7 && (resName === 'PRO' && atomName === 'N')) chemDonor = false;
      if (el === 7 && resName === 'HIS' && (atomName === 'ND1' || atomName === 'NE2')) { chemAcceptor = true; }
      const isAromatic = AROMATIC_RING_ATOMS[resName]?.has(atomName) ?? false;
      const isAcceptor = REPRODUCE_HBOND_DONOR_ACCEPTOR_SWAP ? chemDonor : chemAcceptor;
      const isDonor = REPRODUCE_HBOND_DONOR_ACCEPTOR_SWAP ? chemAcceptor : chemDonor;
      // HydrogenBondingColors.GetColor priority (HydrogenBondingColors.cs:36-42)
      const c = isAcceptor ? HBOND_COLORS.acceptor : isDonor ? HBOND_COLORS.donor : isHydrophobic ? HBOND_COLORS.hydrophobic : isAromatic ? HBOND_COLORS.aromatic : HBOND_COLORS.default;
      return hexToFloat(c);
    }
    case 'Electrostatic':
      // Needs a server potential map (ElectrostaticDensity is surface-only, RenderingViewModel.Surface.cs:116-122): uniform grey.
      return [0.5, 0.5, 0.5];
    case 'Property': {
      const source = coloring.propertySource ?? 'ChainInstance';
      const def = defaultPalette(source);
      const palette: readonly Hex[] = coloring.colors && coloring.colors.length > 0 ? coloring.colors.map(hexFromApi) : def.palette;
      const paletteType = coloring.paletteType ?? def.paletteType;
      const n = palette.length;
      const chainIndex = model.chainIndex[atomIndex]!;
      if (paletteType === 'Default') {
        let index: number;
        switch (source) {
          case 'AtomSerial': {
            cache.serialRange ??= atomSerialRange(model);
            index = Math.trunc(normalise(model.atomSerial[atomIndex]!, cache.serialRange[0], cache.serialRange[1], true) * (n - 1));
            break;
          }
          case 'Occupancy': index = Math.trunc(model.occupancy[atomIndex]! * (n - 1)); break;
          // ChainName is indexed across the workspace (MapChainNames)
          case 'ChainName': index = chainNameKey(model, chainIndex, ctx)[0]; break;
          case 'ChainInstance': index = chainInstanceKey(ctx.entrySerial, chainIndex); break;
          case 'ModelInstance': index = modelInstanceKey(ctx.entrySerial, ctx.modelSerial); break;
          default: return WHITE; // BFactor/AtomName with PaletteType.Default throw in Nanome -> white (:135-143)
        }
        return hexToFloat(paletteIndexed(palette, index));
      }
      let t: number;
      switch (source) {
        case 'AtomSerial': {
          cache.serialRange ??= atomSerialRange(model);
          t = normalise(model.atomSerial[atomIndex]!, cache.serialRange[0], cache.serialRange[1], true);
          break;
        }
        case 'BFactor': t = normalise(model.bfactor[atomIndex]!, model.bfactorRange[0], model.bfactorRange[1], false); break;
        case 'Occupancy': t = model.occupancy[atomIndex]!; break;
        case 'ChainName': { const [k, n] = chainNameKey(model, chainIndex, ctx); t = n <= 1 ? 0 : k / (n - 1); break; }
        // C# picks the float indexer for the int keys here (mixed switch type), i.e. the key is clamped to [0, 1].
        case 'ChainInstance': t = chainInstanceKey(ctx.entrySerial, chainIndex); break;
        case 'ModelInstance': t = modelInstanceKey(ctx.entrySerial, ctx.modelSerial); break;
        default: return WHITE;
      }
      return hexToFloat(paletteInterpolated(palette, t));
    }
  }
  return WHITE;
}

/**
 * Colour `atomIndices` of `model` under `coloring`. `carbonsOnly` wraps the coloring as ElementType(inner): carbons
 * take the given coloring, every other element CPK (ChangeColorTheme.cs:249-260; Coloring.cs:67-74).
 * Returns interleaved rgb floats, one triple per entry of `atomIndices`.
 */
export function colorAtoms(model: NanomeModel, atomIndices: ArrayLike<number>, coloring: ColoringModel, ctx: ColoringContext): Float32Array {
  const out = new Float32Array(atomIndices.length * 3);
  const cache = {};
  const carbonsOnly = coloring.carbonsOnly === true && coloring.kind !== 'ElementType';
  for (let k = 0; k < atomIndices.length; k++) {
    const i = atomIndices[k]!;
    let c: [number, number, number];
    if (carbonsOnly && model.element[i] !== 6) c = hexToFloat(ELEMENT_COLORS.get(model.element[i]!) ?? ELEMENT_COLOR_DEFAULT);
    else c = colorOfAtom(model, i, k, coloring, ctx, cache);
    out[k * 3] = c[0]; out[k * 3 + 1] = c[1]; out[k * 3 + 2] = c[2];
  }
  return out;
}
